import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import {
  createListPageView,
  deleteListPageView,
  getGetListPageStateQueryKey,
  getListPageState,
  setListPageUserState,
  setListPageViewDisplay,
  updateListPageView,
} from '@/api/generated/list-pages/list-pages';
import type { ListPageViewVisibility } from '@/api/generated/schemas';
import { toast } from '@/shared/ui/feedback/toast';

const USER_STATE_DEBOUNCE_MS = 2_000;

export interface ListViewDefinitionInput {
  name: string;
  shared: boolean;
  payload: string;
  displayPayload: string | null;
}

/** Serverový stav šablony (pohledy + user-state) a operace nad centrální persistencí. */
export interface ListPageServerState {
  isPending: boolean;
  isError: boolean;
  views: {
    id: string;
    name: string;
    shared: boolean;
    own: boolean;
    payload: string;
    displayPayload: string | null;
  }[];
  userStatePayload: string | null;
  createView: (input: ListViewDefinitionInput) => Promise<string>;
  updateView: (id: string, input: ListViewDefinitionInput) => Promise<void>;
  deleteView: (id: string) => Promise<void>;
  /** Přeuložení display dat (labely *Ref) — smí kdokoliv, fire-and-forget. */
  setViewDisplay: (id: string, displayPayload: string) => void;
  /** Debounced fire-and-forget zápis user-state (konfigurace sloupců, preference, pořadí záložek). */
  saveUserState: (payload: string) => void;
}

function visibility(shared: boolean): ListPageViewVisibility {
  return (shared ? 'Team' : 'Private') as ListPageViewVisibility;
}

/**
 * Centrální persistence ListPage (`/api/list-pages/*`): jeden GET pro otevření agendy,
 * CRUD pohledů s invalidací a debounced zápis user-state. Selhání zápisů jen toastuje —
 * persistence preferencí nesmí blokovat práci se seznamem.
 */
export function useListPageServerState(listPageKey: string): ListPageServerState {
  const { t } = useTranslation('listPage');
  const queryClient = useQueryClient();
  const stateQueryKey = getGetListPageStateQueryKey(listPageKey);

  const query = useQuery({
    queryKey: stateQueryKey,
    queryFn: ({ signal }) => getListPageState(listPageKey, signal),
    staleTime: 60_000,
  });

  const invalidate = useCallback(
    () => queryClient.invalidateQueries({ queryKey: stateQueryKey }),
    [queryClient, stateQueryKey]
  );

  const persistErrorToast = useCallback(
    () => toast({ severity: 'error', description: t('persistence.saveFailed') }),
    [t]
  );

  // ── user-state: debounce + flush při unmountu (best-effort) ──
  const pendingPayload = useRef<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const flush = useCallback(() => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
    const payload = pendingPayload.current;
    if (payload === null) {
      return;
    }
    pendingPayload.current = null;
    setListPageUserState(listPageKey, { payload }).catch(persistErrorToast);
  }, [listPageKey, persistErrorToast]);

  const saveUserState = useCallback(
    (payload: string) => {
      pendingPayload.current = payload;
      if (timer.current) {
        clearTimeout(timer.current);
      }
      timer.current = setTimeout(flush, USER_STATE_DEBOUNCE_MS);
    },
    [flush]
  );

  useEffect(() => flush, [flush]);

  return useMemo(
    () => ({
      isPending: query.isPending,
      isError: query.isError,
      views: (query.data?.views ?? []).map((view) => ({
        id: view.id,
        name: view.name,
        shared: view.visibility === 'Team',
        own: view.isOwn,
        payload: view.payload,
        displayPayload: view.displayPayload ?? null,
      })),
      userStatePayload: query.data?.userStatePayload ?? null,

      createView: async (input) => {
        const id = await createListPageView(listPageKey, {
          name: input.name,
          visibility: visibility(input.shared),
          payload: input.payload,
          displayPayload: input.displayPayload,
        });
        await invalidate();
        return id;
      },
      updateView: async (id, input) => {
        await updateListPageView(id, {
          name: input.name,
          visibility: visibility(input.shared),
          payload: input.payload,
          displayPayload: input.displayPayload,
        });
        await invalidate();
      },
      deleteView: async (id) => {
        await deleteListPageView(id);
        await invalidate();
      },
      setViewDisplay: (id, displayPayload) => {
        setListPageViewDisplay(id, { displayPayload }).catch(persistErrorToast);
      },
      saveUserState,
    }),
    [
      query.isPending,
      query.isError,
      query.data,
      listPageKey,
      invalidate,
      saveUserState,
      persistErrorToast,
    ]
  );
}
